import React, { useContext, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { MdLockOpen } from 'react-icons/md';
import { Input, Button, Alert } from 'antd';
import FormControl from '../../../components/UI/FormControl/FormControl';
import { AuthContext } from '../../../context/AuthProvider';
import { registerUser } from 'Api/authCalls';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import { handleAxiosError } from 'Api/utils';

interface SignUpFormData {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  confirmPassword: string;
  termsAndConditions: boolean;
  phone: string;
}

const SignUpForm: React.FC = () => {
  const { signUp, loggedIn } = useContext(AuthContext);
  const [message, setMessage] = useState('');
  const [errorMessages, setErrorMessages] = useState({
    username: null,
  });
  const [formStatus, setFormStatus] = useState('register');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const {
    control,
    watch,
    formState: { errors },
    handleSubmit,
  } = useForm<SignUpFormData>({
    mode: 'onChange',
  });

  const password = watch('password');
  const confirmPassword = watch('confirmPassword');
  // const [message, setMessage] = useState<string | null>(null);

  const onSubmit = async (data: SignUpFormData) => {
    try {
      const formData = new FormData();
      formData.append('username', data.email);
      formData.append('password', data.password);
      formData.append('confirmPassword', data.confirmPassword);
      formData.append('first_name', data.first_name);
      formData.append('last_name', data.last_name);
      formData.append('phone', data.phone);
      formData.append('termsAndConditions', String(data.termsAndConditions));

      setLoading(true);
      const response = await registerUser(formData);
      if (response.status === 200) {
        setMessage(response.data.msg);
        setFormStatus('success');
      } else {
        setMessage(
          response.data.msg || 'Something went wrong, please try again later'
        );
        setErrorMessages(response.data.error);
        setFormStatus('failure');
        setLoading(false);
      }
    } catch (error: any) {
      console.error('Registration failed:', error);
      const errResp = handleAxiosError(error);
      setFormStatus('failure');
      setLoading(false);
      setErrorMessages(errResp);
      console.log(errResp);
    }
  };

  if (formStatus === 'success') {
    return (
      <Alert
        message={message || 'Registration successful. Verification email sent.'}
        type='success'
      />
    );
  }
  const validateNoDigits = (value: any) => {
    return /^[^0-9]*$/.test(value) || 'This field should not contain digits!';
  };

  return (
    <>
      {formStatus === 'failure' && (
        <div style={{ marginBottom: '20px' }}>
          <Alert
            message={message || 'Registration failed. Please try again.'}
            type='error'
          />
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <FormControl
          label='First Name'
          htmlFor='first_name'
          error={
            errors.first_name && (
              <>
                {errors.first_name?.type === 'required' && (
                  <span>This field is required!</span>
                )}
                {errors.first_name?.type === 'validate' && (
                  <span>{errors.first_name.message}</span>
                )}
              </>
            )
          }
        >
          <Controller
            name='first_name'
            defaultValue=''
            control={control}
            rules={{ required: true, validate: validateNoDigits }}
            render={({ field: { onChange, onBlur, value } }) => (
              <Input onChange={onChange} onBlur={onBlur} value={value} />
            )}
          />
        </FormControl>
        <FormControl
          label='Last Name'
          htmlFor='last_name'
          error={
            errors.last_name && (
              <>
                {errors.last_name?.type === 'required' && (
                  <span>This field is required!</span>
                )}
                {errors.last_name?.type === 'validate' && (
                  <span>{errors.last_name.message}</span>
                )}
              </>
            )
          }
        >
          <Controller
            name='last_name'
            defaultValue=''
            control={control}
            rules={{ required: true, validate: validateNoDigits }}
            render={({ field: { onChange, onBlur, value } }) => (
              <Input onChange={onChange} onBlur={onBlur} value={value} />
            )}
          />
        </FormControl>
        <FormControl
          label='Email'
          htmlFor='email'
          error={
            errors.email && (
              <>
                {errors.email?.type === 'required' && (
                  <span>This field is required!</span>
                )}
                {errors.email?.type === 'pattern' && (
                  <span>Please enter a valid email address</span>
                )}
              </>
            )
          }
        >
          <Controller
            name='email'
            defaultValue=''
            control={control}
            rules={{
              required: true,
              pattern: /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/,
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <Input
                type='email'
                onChange={onChange}
                onBlur={onBlur}
                value={value}
              />
            )}
          />
          {errorMessages.username && (
            <Alert
              message={errorMessages.username}
              showIcon={true}
              type='error'
            />
          )}
        </FormControl>
        <FormControl
          label='Phone Number'
          htmlFor='phoneNumber'
          error={
            errors.phone && (
              <>
                {errors.phone?.type === 'required' && (
                  <span>This field is required!</span>
                )}
                {errors.phone?.type === 'minLength' && (
                  <span>Phone Number must be at lest 11 characters!</span>
                )}
                {errors.phone?.type === 'maxLength' && (
                  <span>
                    Phone Number must not be longer than 11 characters!
                  </span>
                )}
              </>
            )
          }
        >
          <Controller
            name='phone'
            defaultValue=''
            control={control}
            rules={{ required: true, minLength: 11, maxLength: 11 }}
            render={({ field: { onChange, onBlur, value } }) => (
              <Input
                type='number'
                onChange={onChange}
                onBlur={onBlur}
                value={value}
              />
            )}
          />
          {errorMessages.username && (
            <Alert
              message={errorMessages.username}
              showIcon={true}
              type='error'
            />
          )}
        </FormControl>
        <FormControl
          label='Password'
          htmlFor='password'
          error={
            errors.password && (
              <>
                {errors.password?.type === 'required' && (
                  <span>This field is required!</span>
                )}
                {errors.password?.type === 'minLength' && (
                  <span>Password must be at lest 6 characters!</span>
                )}
                {errors.password?.type === 'maxLength' && (
                  <span>Password must not be longer than 20 characters!</span>
                )}
              </>
            )
          }
        >
          <Controller
            name='password'
            defaultValue=''
            control={control}
            rules={{ required: true, minLength: 6, maxLength: 20 }}
            render={({ field: { onChange, onBlur, value } }) => (
              <Input.Password
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                autoComplete='new-password'
              />
            )}
          />
        </FormControl>
        <FormControl
          label='Confirm password'
          htmlFor='confirmPassword'
          error={
            confirmPassword &&
            password !== confirmPassword && (
              <span>Your password is not same!</span>
            )
          }
        >
          <Controller
            name='confirmPassword'
            defaultValue=''
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, onBlur, value } }) => (
              <Input.Password
                onChange={onChange}
                onBlur={onBlur}
                value={value}
              />
            )}
          />
        </FormControl>

        <Button
          className='signin-btn bg'
          type='primary'
          htmlType='submit'
          size='large'
          style={{ width: '100%' }}
        >
          {loading ? (
            <div className='loader'>
              <div className='bar1'></div>
              <div className='bar2'></div>
              <div className='bar3'></div>
              <div className='bar4'></div>
              <div className='bar5'></div>
              <div className='bar6'></div>
              <div className='bar7'></div>
              <div className='bar8'></div>
              <div className='bar9'></div>
              <div className='bar10'></div>
              <div className='bar11'></div>
              <div className='bar12'></div>
            </div>
          ) : (
            <div className='email'>Sign Up</div>
          )}
        </Button>
      </form>
    </>
  );
};

export default SignUpForm;
